import { ChevronsUpDown } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { ROLE_LABELS } from '../types/models';

/**
 * The current company, always visible at the head of the sidebar: a login can belong to
 * several companies and filing work against the wrong one is unrecoverable. With more
 * than one membership the whole card is a native select, so switching stays keyboard-
 * and screen-reader-accessible in both Chrome and Safari.
 */
export function CompanyPicker() {
  const { memberships, activeCompanyId, activeRole, setActiveCompanyId, companyName } = useAuth();
  if (memberships.length === 0) return null;

  const others = memberships.length - 1;
  const detail = [
    activeRole ? ROLE_LABELS[activeRole] : null,
    others > 0 ? `${others} other compan${others === 1 ? 'y' : 'ies'}` : null,
  ].filter(Boolean).join(' · ');

  const card = (
    <div className="flex items-center gap-2.5 rounded-[12px] border border-hair px-3 py-2.5 transition-colors duration-[180ms] ease-out group-hover:bg-accenttint group-focus-within:ring-2 group-focus-within:ring-accent">
      <span className="h-2 w-2 shrink-0 rounded-full bg-st-completed" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] font-semibold text-ink">{companyName(activeCompanyId)}</span>
        {detail && <span className="block truncate text-[12.5px] text-ink3">{detail}</span>}
      </span>
      {others > 0 && <ChevronsUpDown size={16} className="shrink-0 text-ink3" aria-hidden />}
    </div>
  );

  if (others === 0) return card;

  return (
    <div className="group relative">
      {card}
      <select
        aria-label="Switch company"
        value={activeCompanyId ?? ''}
        onChange={(e) => setActiveCompanyId(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {memberships.map((m) => (
          <option key={m.companyId} value={m.companyId}>
            {companyName(m.companyId)} · {ROLE_LABELS[m.role]}
          </option>
        ))}
      </select>
    </div>
  );
}
